import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Badge,
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@roger/ui-components';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  ImageIcon,
  Layers,
  Loader2,
  MessageSquare,
  MousePointer2,
  RefreshCw,
  SendHorizonal,
  Settings,
  Sparkles,
  X,
} from 'lucide-react';
import type {
  BespokeTemplateCreate,
  BespokeTemplateResponse,
  BespokeTemplateUpdate,
  BespokeTemplateStatus,
  FormSchemaVersion,
  RogerDocFormTypeCatalogEntry,
  VersionedQuestionSchema,
  VibecoderModelOption,
} from '../../services/api/types.gen';
import type { RogerDocFormType } from '../../services/api/models/RogerDocFormType';
import type { VisitTypeId } from '../../services/api/models/VisitTypeId';
import {
  getVersionedQuestionSchemaRogerdocV2QuestionSchemaPost,
  listVibecoderModelsVibecoderLayoutAssistantModelsGet,
} from '../../services/api/sdk.gen';
import { fetchRogerEmrFormSchemaVersions } from './fetchRogerEmrFormSchemaVersions';
import { useStore } from '../../store';
import { useToast } from '../../hooks/useToastContext';
import type { Question, FormValue } from '../../components/oasis/types';
import { BespokeLayoutRenderer } from '../../components/oasis/bespoke/layout/BespokeLayoutRenderer';
import {
  tryParseLayoutJson,
  getLayoutCompileError,
} from '../../components/oasis/bespoke/layout/tryParseLayoutJson';
import { collectSectionCoverageQids } from '../../components/oasis/bespoke/verification/bespokeCoverageSearch';
import type { CoverageSearchResult } from '../../components/oasis/bespoke/verification/bespokeCoverageSearch';
import { buildCoverageFixAssistantPrompt } from './layoutCoverageFixPrompt';
import {
  collectSectionLayoutCoverageIssues,
  formatLayoutPlainCssHiddenSummary,
  formatRemotePushTimeLabel,
  inactiveLayoutVersionIndicator,
  isEditingActiveLayoutVersion,
  layoutGenerationKey,
  sectionEditorDraftKey,
  type RemotePushInfo,
} from './formSchemaUtils';
import { formatLayoutSchemaGateIssueSummary } from '../../components/oasis/bespoke/layout/collectLayoutSchemaGateIssues';
import type { FormTreeQuestion } from './formSchemaTypes';
import type { VibecoderChatImage, VibecoderChatTurn } from './vibecoderChat';
import { composeVibecodeLlmBundle } from '../../components/oasis/bespoke/layout-studio/composeVibecodeLlmBundle';
import { formTreeSectionToQuestions } from '../../components/oasis/bespoke/layout-studio/formTreeToQuestions';
import {
  type FormSchemaStudioPick,
  makeFormSchemaVersionPickId,
} from './schemaStudioPick';
import { formatSectionDropdownLabel } from './formatSectionDropdownLabel';
import { SectionLayoutJsonDialog } from './SectionLayoutJsonDialog';
import {
  formatPreviewElementContextForLlm,
  usePreviewElementPicker,
} from './usePreviewElementPicker';

/** Section bucket shape from `schema_data` (RogerDoc question tree). */
type StudioFormTree = Record<string, unknown>;

/** Stable empty array so reads from an unhydrated draft don't churn memos. */
const NO_CHAT_MESSAGES: VibecoderChatTurn[] = [];

export type TemplateVibecoderDraft = {
  schemaVersionName: string;
  schemaVersionValue?: string | null;
  rogerDocFormType: RogerDocFormType;
  sectionName: string;
  name: string;
  description: string;
  layoutJsonText: string;
  layoutStatus: BespokeTemplateStatus;
};

const LARGE_LAYOUT_TEXT_LENGTH = 25_000;
const LAYOUT_PARSE_DEBOUNCE_MS = 300;

const DEFAULT_TEMPERATURE = 0.2;
const TEMPERATURE_MIN = 0;
const TEMPERATURE_MAX = 1;
const TEMPERATURE_STEP = 0.1;

const VIBECODER_IMAGE_MAX_BYTES = 5 * 1024 * 1024;
const VIBECODER_IMAGE_MEDIA_TYPES = new Set<VibecoderChatImage['media_type']>([
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
]);

function readVibecoderImage(file: File): Promise<VibecoderChatImage> {
  const mediaType = file.type as VibecoderChatImage['media_type'];
  if (!VIBECODER_IMAGE_MEDIA_TYPES.has(mediaType)) {
    return Promise.reject(new Error('Use a PNG, JPEG, GIF, or WebP image.'));
  }
  if (file.size > VIBECODER_IMAGE_MAX_BYTES) {
    return Promise.reject(new Error('Image must be 5 MB or smaller.'));
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : '';
      const [, data] = result.split(',', 2);
      if (!data) {
        reject(new Error('Could not read image data.'));
        return;
      }
      resolve({ media_type: mediaType, data, filename: file.name });
    };
    reader.onerror = () => reject(new Error('Could not read image file.'));
    reader.readAsDataURL(file);
  });
}

export interface SectionEditorPanelProps {
  editingTemplate: BespokeTemplateResponse | null;
  initialDraft?: TemplateVibecoderDraft | null;
  onSaveJson: (
    data: BespokeTemplateCreate | BespokeTemplateUpdate
  ) => void | Promise<void>;
  saving: boolean;
  /** Shared catalog from Form Schema Manager (single HTTP fetch upstream). */
  formTypeCatalog: RogerDocFormTypeCatalogEntry[];
  catalogLoading: boolean;
  /**
   * Form type selected in Form Schema Manager when opening a template for edit.
   * Avoids scanning every visit type via form-schemas/current.
   */
  selectedRogerDocFormType: RogerDocFormType | null;
  /** Live/Paused primary template id for the open section, if any. */
  activeTemplateId?: string | null;
  /**
   * MCP push info for the currently-open template, if any (EMR-277). Drives
   * the "Updated remotely <time>" header pill. Unlike the section-tab dot,
   * this does not clear when the editor opens the template — only a local
   * save (or switching to a different section/version) clears it.
   */
  remotePush?: RemotePushInfo | null;
  layoutJsonOpen: boolean;
  onLayoutJsonOpenChange: (open: boolean) => void;
}

export function SectionEditorPanel({
  editingTemplate: initial,
  initialDraft,
  onSaveJson,
  saving,
  formTypeCatalog,
  selectedRogerDocFormType,
  activeTemplateId = null,
  remotePush = null,
  layoutJsonOpen,
  onLayoutJsonOpenChange,
}: SectionEditorPanelProps) {
  const toast = useToast();

  // Section + version identity for this editor instance; drives the per-section
  // working-draft key in the store.
  const targetSchemaVersionName =
    initial?.schema_version_name ?? initialDraft?.schemaVersionName ?? '';
  const targetSectionName =
    initial?.section_name ?? initialDraft?.sectionName ?? '';
  const targetFormType =
    initialDraft?.rogerDocFormType ?? selectedRogerDocFormType ?? null;
  const draftKey =
    targetFormType != null
      ? sectionEditorDraftKey(
          targetFormType,
          targetSchemaVersionName,
          targetSectionName
        )
      : layoutGenerationKey(targetSchemaVersionName, targetSectionName);

  const [studioFormType, setStudioFormType] = useState<RogerDocFormType | null>(
    null
  );
  const [studioVersions, setStudioVersions] = useState<FormSchemaVersion[]>([]);

  useEffect(() => {
    if (formTypeCatalog.length === 0) return;
    if (initial?.layout_json) return;
    if (initialDraft) return;
    setStudioFormType(prev => {
      if (prev != null && formTypeCatalog.some(r => r.form_type === prev)) {
        return prev;
      }
      return formTypeCatalog[0].form_type;
    });
  }, [formTypeCatalog, initial?.layout_json, initialDraft]);

  useEffect(() => {
    if (!initialDraft) return;
    setStudioFormType(initialDraft.rogerDocFormType);
  }, [initialDraft]);

  useEffect(() => {
    if (!initial?.layout_json || formTypeCatalog.length === 0) return;
    if (
      selectedRogerDocFormType == null ||
      !formTypeCatalog.some(r => r.form_type === selectedRogerDocFormType)
    ) {
      return;
    }
    setStudioFormType(selectedRogerDocFormType);
  }, [
    initial?.id,
    initial?.layout_json,
    formTypeCatalog,
    selectedRogerDocFormType,
  ]);

  useEffect(() => {
    const entry = formTypeCatalog.find(r => r.form_type === studioFormType);
    const vt = entry?.canonical_visit_type_id;
    if (!vt) {
      setStudioVersions([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const versions = await fetchRogerEmrFormSchemaVersions(vt);
        if (!cancelled) {
          setStudioVersions(versions);
        }
      } catch {
        if (!cancelled) setStudioVersions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [studioFormType, formTypeCatalog]);

  const [schemaPickId, setSchemaPickId] = useState('');

  useEffect(() => {
    setSchemaPickId('');
  }, [studioFormType]);

  const sectionName = targetSectionName;
  const [previewLayoutJsonText, setPreviewLayoutJsonText] = useState('');
  const previewColumnRef = useRef<HTMLDivElement | null>(null);
  /** Docked AI chat window over the bottom half of the form display. */
  const [chatOpen, setChatOpen] = useState(false);
  /** Collapsible validation ("scrubber") panel above the preview. */
  const [validationOpen, setValidationOpen] = useState(true);

  const [chatInput, setChatInput] = useState('');
  const [chatImages, setChatImages] = useState<VibecoderChatImage[]>([]);

  // Per-section working state lives in the store (keyed by section), so chat
  // requests and unsaved edits survive section switches. The parent hydrates
  // `editorDraftByKey[draftKey]` before rendering this panel.
  const editorDraft = useStore(s => s.editorDraftByKey[draftKey]);
  const setSectionEditorField = useStore(s => s.setSectionEditorField);
  const sendSectionEditorChat = useStore(s => s.sendSectionEditorChat);
  const revertSectionEditorDraft = useStore(s => s.revertSectionEditorDraft);
  const isEditingPublishedJson = editorDraft?.editingTemplateId != null;
  const layoutJsonText = editorDraft?.layoutJsonText ?? '';
  const name = editorDraft?.name ?? '';
  const description = editorDraft?.description ?? '';
  const layoutStatus: BespokeTemplateStatus = editorDraft?.status ?? 'live';
  const chatMessages = editorDraft?.chatMessages ?? NO_CHAT_MESSAGES;
  const chatSending = editorDraft?.chatPending ?? false;
  const isDirty =
    !!editorDraft &&
    (editorDraft.layoutJsonText !== editorDraft.baseline.layoutJsonText ||
      editorDraft.name !== editorDraft.baseline.name ||
      editorDraft.description !== editorDraft.baseline.description ||
      editorDraft.status !== editorDraft.baseline.status);

  const editingActiveLayoutVersion = isEditingActiveLayoutVersion({
    editingTemplateId: editorDraft?.editingTemplateId ?? null,
    activeTemplateId,
    layoutStatus,
  });
  const inactiveLayoutIndicator = editingActiveLayoutVersion
    ? null
    : inactiveLayoutVersionIndicator({
        editingTemplateId: editorDraft?.editingTemplateId ?? null,
        activeTemplateId,
        layoutStatus,
      });

  const [availableModels, setAvailableModels] = useState<
    VibecoderModelOption[]
  >([]);
  const vibecoderModelId = useStore(s => s.vibecoderModelId);
  const vibecoderTemperature = useStore(s => s.vibecoderTemperature);
  const setVibecoderModelId = useStore(s => s.setVibecoderModelId);
  const setVibecoderTemperature = useStore(s => s.setVibecoderTemperature);
  const [settingsPopoverOpen, setSettingsPopoverOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await listVibecoderModelsVibecoderLayoutAssistantModelsGet(
          {}
        );
        if (cancelled) return;
        const data = res?.data;
        if (!data) return;
        setAvailableModels(data.models);
      } catch (err) {
        console.error('Failed to load vibecoder models:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Prefer the persisted pick if it's still registered; otherwise use the
  // first model the backend returned. A stale ``vibecoderModelId`` (model
  // removed from the registry) silently falls through to the same first
  // entry, so the picker never gets stuck on an id the backend rejects.
  const selectedModel = useMemo<VibecoderModelOption | null>(() => {
    if (availableModels.length === 0) return null;
    const fromSettings = availableModels.find(m => m.id === vibecoderModelId);
    if (fromSettings) return fromSettings;
    return availableModels[0];
  }, [availableModels, vibecoderModelId]);

  const effectiveTemperature = useMemo<number>(() => {
    if (!selectedModel) return DEFAULT_TEMPERATURE;
    if (!selectedModel.supports_temperature) {
      return selectedModel.default_temperature;
    }
    if (vibecoderTemperature == null || Number.isNaN(vibecoderTemperature)) {
      return selectedModel.default_temperature;
    }
    return Math.min(
      TEMPERATURE_MAX,
      Math.max(TEMPERATURE_MIN, vibecoderTemperature)
    );
  }, [selectedModel, vibecoderTemperature]);
  const {
    previewPaneRef,
    previewPickerActive,
    previewHoverBox,
    pickedPreviewElement,
    setPickedPreviewElement,
    clearPreviewPicker,
    handlePreviewPickerMove,
    handlePreviewPickerClick,
    togglePreviewPicker,
    setPreviewHoverBox,
  } = usePreviewElementPicker();

  const chatTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const chatImageInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const el = chatTextareaRef.current;
    if (!el) return;
    el.style.height = '80px';
    const nextHeight = Math.min(Math.max(el.scrollHeight, 80), 160);
    el.style.height = `${nextHeight}px`;
    el.style.overflowY = el.scrollHeight > 128 ? 'auto' : 'hidden';
  }, [chatInput]);

  // Reset transient composer/UI state when the edited section/template changes;
  // the working fields + chat history persist per section in the store.
  useEffect(() => {
    setChatInput('');
    setChatImages([]);
    setChatOpen(false);
    clearPreviewPicker();
  }, [initial?.id, initialDraft, clearPreviewPicker]);

  const [sectionQuestions, setSectionQuestions] = useState<Question[]>([]);
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [questionsResolved, setQuestionsResolved] = useState(false);
  const [studioAnswers, setStudioAnswers] = useState<Record<string, FormValue>>(
    {}
  );

  const studioCatalogEntry = useMemo(
    () => formTypeCatalog.find(r => r.form_type === studioFormType) ?? null,
    [formTypeCatalog, studioFormType]
  );

  const canonicalVisitForStudio: VisitTypeId | null =
    studioCatalogEntry?.canonical_visit_type_id ?? null;

  const studioSchemaPicks = useMemo((): FormSchemaStudioPick[] => {
    if (
      canonicalVisitForStudio == null ||
      studioFormType == null ||
      studioCatalogEntry == null
    ) {
      return [];
    }
    const displayLabel = studioCatalogEntry.display_label;
    const picks = studioVersions.map(v => ({
      id: makeFormSchemaVersionPickId(v),
      rogerDocFormType: studioFormType,
      visitType: canonicalVisitForStudio,
      isOasis: studioCatalogEntry.is_oasis,
      formTypeDisplayLabel: displayLabel,
      label: [
        v.name,
        ...(v.version != null && `${v.version}`.length > 0
          ? [`v${v.version}`]
          : []),
      ].join(' · '),
      schemaVersion: v,
    }));
    return picks.sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })
    );
  }, [
    studioVersions,
    studioFormType,
    canonicalVisitForStudio,
    studioCatalogEntry,
  ]);

  const resolveStudioSectionQuestions = useCallback(
    async (
      pickId: string,
      sectionKey: string,
      _fallbackSchemaVersionName?: string | null
    ): Promise<Question[]> => {
      const trimmed = pickId.trim();
      const pick = studioSchemaPicks.find(p => p.id === trimmed);
      if (!pick) return [];

      try {
        const fv = pick.schemaVersion;
        const res =
          await getVersionedQuestionSchemaRogerdocV2QuestionSchemaPost({
            query: { visit_type: pick.visitType },
            body: {
              schema_version: {
                name: fv.name,
                version: fv.version ?? null,
              },
            },
          });
        const payload = res?.data as VersionedQuestionSchema | undefined;
        const tree = payload?.schema_data as StudioFormTree | null | undefined;
        const rows = tree?.[sectionKey];
        if (!Array.isArray(rows)) return [];
        return formTreeSectionToQuestions(rows);
      } catch {
        return [];
      }
    },
    [studioSchemaPicks]
  );

  useEffect(() => {
    const t = initial;
    if (!t?.layout_json) return;
    const pid =
      studioSchemaPicks.find(
        p => p.schemaVersion.name === t.schema_version_name
      )?.id ?? '';
    setSchemaPickId(pid);
  }, [initial, studioSchemaPicks]);

  useEffect(() => {
    if (!initialDraft || initial?.layout_json) return;
    const pid =
      studioSchemaPicks.find(
        p =>
          p.schemaVersion.name === initialDraft.schemaVersionName &&
          (initialDraft.schemaVersionValue == null ||
            p.schemaVersion.version === initialDraft.schemaVersionValue)
      )?.id ?? '';
    if (pid) {
      setSchemaPickId(pid);
    }
  }, [initialDraft, initial?.layout_json, studioSchemaPicks]);

  const activePick = useMemo((): FormSchemaStudioPick | undefined => {
    if (schemaPickId) {
      return studioSchemaPicks.find(p => p.id === schemaPickId);
    }
    if (initial?.layout_json) {
      return studioSchemaPicks.find(
        p => p.schemaVersion.name === initial.schema_version_name
      );
    }
    return undefined;
  }, [schemaPickId, studioSchemaPicks, initial]);

  useEffect(() => {
    let cancelled = false;
    async function loadQuestions() {
      setQuestionsResolved(false);
      const editJson = !!initial?.layout_json;
      const sec = editJson ? initial!.section_name : sectionName.trim();

      if (!sec.trim()) {
        setSectionQuestions([]);
        return;
      }

      const fallbackName = editJson ? initial!.schema_version_name : undefined;

      let effectivePickId = schemaPickId.trim();
      if (!effectivePickId && fallbackName) {
        effectivePickId =
          studioSchemaPicks.find(p => p.schemaVersion.name === fallbackName)
            ?.id ?? '';
      }

      if (!effectivePickId) {
        setSectionQuestions([]);
        return;
      }

      setQuestionsLoading(true);
      try {
        const qs = await resolveStudioSectionQuestions(
          effectivePickId,
          sec.trim(),
          fallbackName
        );
        if (!cancelled) {
          setSectionQuestions(qs);
          setQuestionsResolved(true);
        }
      } catch (e) {
        console.error('Failed to fetch section schema for vibecoder:', e);
        if (!cancelled) {
          setSectionQuestions([]);
          setQuestionsResolved(true);
        }
      } finally {
        if (!cancelled) setQuestionsLoading(false);
      }
    }

    void loadQuestions();
    return () => {
      cancelled = true;
    };
  }, [
    schemaPickId,
    sectionName,
    initial,
    studioSchemaPicks,
    resolveStudioSectionQuestions,
  ]);

  useEffect(() => {
    const delay =
      layoutJsonText.length >= LARGE_LAYOUT_TEXT_LENGTH
        ? LAYOUT_PARSE_DEBOUNCE_MS
        : 0;
    const handle = window.setTimeout(() => {
      setPreviewLayoutJsonText(layoutJsonText);
    }, delay);
    return () => window.clearTimeout(handle);
  }, [layoutJsonText]);

  const parsedLayout = useMemo(
    () => tryParseLayoutJson(previewLayoutJsonText),
    [previewLayoutJsonText]
  );
  const parsedLayoutIdentity =
    parsedLayout.ok && parsedLayout.layout
      ? JSON.stringify(parsedLayout.layout)
      : parsedLayout.ok
        ? '[]'
        : parsedLayout.error;
  const layoutParsePending = previewLayoutJsonText !== layoutJsonText;

  useEffect(() => {
    if (!parsedLayout.ok || !parsedLayout.layout) return;
    setStudioAnswers({});
  }, [parsedLayout.ok, parsedLayoutIdentity]);

  /** All QIDs in the loaded section subtree (top-level + nested `subitems`), same scope as coverage checks + `schemaMap`. */
  const sectionCoverageQids = useMemo(
    () => collectSectionCoverageQids(sectionQuestions),
    [sectionQuestions]
  );
  const layoutCoverageIssues = useMemo(() => {
    if (
      layoutParsePending ||
      !parsedLayout.ok ||
      !parsedLayout.layout ||
      sectionQuestions.length === 0
    ) {
      return null;
    }
    return collectSectionLayoutCoverageIssues(
      parsedLayout.layout,
      sectionQuestions
    );
  }, [
    layoutParsePending,
    parsedLayout.ok,
    parsedLayoutIdentity,
    sectionQuestions,
  ]);

  /** Static field coverage: section QIDs vs answer-control qids declared in layout JSON (both conditional branches). */
  const fieldCoverage = useMemo((): CoverageSearchResult | null => {
    if (!layoutCoverageIssues?.parseOk) return null;
    const uncoveredSet = new Set(layoutCoverageIssues.uncoveredQids);
    return {
      covered: sectionCoverageQids.filter(q => !uncoveredSet.has(q)),
      uncovered: layoutCoverageIssues.uncoveredQids,
    };
  }, [layoutCoverageIssues, sectionCoverageQids]);

  const strayLayoutQids = layoutCoverageIssues?.strayQids ?? [];
  const radioOptionCoverageIssues = layoutCoverageIssues?.radioMismatches ?? [];
  const cssHiddenCoverageIssues = layoutCoverageIssues?.cssHiddenQids ?? [];
  const schemaGateCoverageIssues = layoutCoverageIssues?.schemaGateIssues ?? [];
  const missingCmsVisibleLabelIssues =
    layoutCoverageIssues?.missingCmsVisibleLabels ?? [];

  const layoutCompileError = useMemo(() => {
    if (layoutParsePending || layoutJsonText.trim() === '') return null;
    return getLayoutCompileError(previewLayoutJsonText);
  }, [layoutParsePending, layoutJsonText, previewLayoutJsonText]);

  const targetN = sectionQuestions.length > 0 ? sectionCoverageQids.length : 0;

  const matchedFieldCount =
    fieldCoverage && targetN > 0 ? fieldCoverage.covered.length : null;

  const llmVisitSummary =
    activePick != null ? activePick.formTypeDisplayLabel : null;

  const buildContextBundleOrNull = useCallback((): string | null => {
    if (sectionQuestions.length === 0) return null;
    const verName = (
      initial?.schema_version_name?.trim() ||
      activePick?.schemaVersion.name.trim() ||
      ''
    ).trim();
    const sec = (
      sectionName.trim() ||
      initial?.section_name?.trim() ||
      ''
    ).trim();
    if (!verName || !sec || !activePick) return null;
    return composeVibecodeLlmBundle({
      visitTypeSummary: llmVisitSummary ?? verName,
      schemaVersionName: verName,
      sectionName: sec,
      questions: sectionQuestions,
      canonicalVisitTypeId:
        activePick.visitType ?? canonicalVisitForStudio ?? null,
      isOasis: activePick.isOasis,
    });
  }, [
    sectionQuestions,
    activePick,
    canonicalVisitForStudio,
    initial,
    sectionName,
    llmVisitSummary,
  ]);

  /** Allow assistant send when editing an existing template, or when draft form type + version + section are chosen. */
  const canSendLayoutAssistantMessage = useMemo(
    () =>
      editorDraft?.editingTemplateId != null ||
      (studioFormType != null &&
        schemaPickId.trim() !== '' &&
        sectionName.trim() !== ''),
    [editorDraft?.editingTemplateId, studioFormType, schemaPickId, sectionName]
  );

  const hasCoverageGapsForAssistant = useMemo(
    () =>
      parsedLayout.ok &&
      !layoutParsePending &&
      sectionQuestions.length > 0 &&
      ((fieldCoverage?.uncovered.length ?? 0) > 0 ||
        strayLayoutQids.length > 0 ||
        radioOptionCoverageIssues.length > 0 ||
        cssHiddenCoverageIssues.length > 0 ||
        schemaGateCoverageIssues.length > 0 ||
        missingCmsVisibleLabelIssues.length > 0),
    [
      parsedLayout.ok,
      layoutParsePending,
      sectionQuestions,
      fieldCoverage,
      strayLayoutQids,
      radioOptionCoverageIssues,
      cssHiddenCoverageIssues,
      schemaGateCoverageIssues,
      missingCmsVisibleLabelIssues,
    ]
  );

  const copyLayoutJsonText = useCallback(
    async (text: string) => {
      if (!text.trim()) {
        toast.error('Nothing to copy', 'Roger Layout Schema is empty.');
        return;
      }
      try {
        await navigator.clipboard.writeText(text);
        toast.success('Copied', 'Roger Layout Schema copied to clipboard.');
      } catch {
        toast.error('Clipboard failed', 'Could not write to clipboard.');
      }
    },
    [toast]
  );

  const copyLlmBundle = useCallback(async () => {
    const blob = buildContextBundleOrNull();
    if (!blob) {
      toast.error('Nothing to copy', 'Load a section schema first.');
      return;
    }
    try {
      await navigator.clipboard.writeText(blob);
      toast.success(
        'Copied',
        'LLM context bundle (schema + DSL only) copied to clipboard.'
      );
    } catch {
      toast.error('Clipboard failed', 'Could not write to clipboard.');
    }
  }, [buildContextBundleOrNull, toast]);

  const handleAttachChatImage = useCallback(
    async (file: File | undefined) => {
      if (!file) return;
      try {
        const image = await readVibecoderImage(file);
        setChatImages([image]);
      } catch (err) {
        toast.error(
          'Image not attached',
          err instanceof Error ? err.message : 'Could not attach image.'
        );
      } finally {
        if (chatImageInputRef.current) {
          chatImageInputRef.current.value = '';
        }
      }
    },
    [toast]
  );

  const sendVibecoderChat = useCallback(async () => {
    const text = chatInput.trim();
    if (
      (!text && chatImages.length === 0 && pickedPreviewElement == null) ||
      chatSending ||
      !canSendLayoutAssistantMessage
    ) {
      return;
    }
    const bundle = buildContextBundleOrNull();
    if (!bundle) {
      toast.error(
        'Missing context',
        'Pick schema version, section, and wait for questions to load.'
      );
      return;
    }
    const userTurn: VibecoderChatTurn = {
      role: 'user',
      content: text,
      llmContent: `${text}${formatPreviewElementContextForLlm(
        pickedPreviewElement
      )}`,
      ...(chatImages.length > 0 ? { images: chatImages } : {}),
    };
    // Auto-close the docked chat on send; the request runs in the store keyed by
    // this section, so switching sections keeps it running and its result lands
    // back on this section's working copy.
    setChatOpen(false);
    setChatInput('');
    setChatImages([]);
    setPickedPreviewElement(null);
    await sendSectionEditorChat({
      key: draftKey,
      userTurn,
      contextBundle: bundle,
      questions: sectionQuestions as FormTreeQuestion[],
      toast,
    });
  }, [
    chatInput,
    chatImages,
    pickedPreviewElement,
    chatSending,
    canSendLayoutAssistantMessage,
    buildContextBundleOrNull,
    sendSectionEditorChat,
    draftKey,
    sectionQuestions,
    setPickedPreviewElement,
    toast,
  ]);

  const requestAssistantCoverageFix = useCallback(async () => {
    if (
      chatSending ||
      !hasCoverageGapsForAssistant ||
      !fieldCoverage ||
      !parsedLayout.ok
    ) {
      return;
    }
    const bundle = buildContextBundleOrNull();
    if (!bundle) {
      toast.error('Missing context', 'Load the section schema first.');
      return;
    }
    const prompt = buildCoverageFixAssistantPrompt({
      uncoveredQids: fieldCoverage.uncovered,
      strayQids: strayLayoutQids,
      radioMismatches: radioOptionCoverageIssues,
      cssHiddenQids: cssHiddenCoverageIssues,
      schemaGateIssues: schemaGateCoverageIssues,
      missingCmsVisibleLabels: missingCmsVisibleLabelIssues,
    });
    await sendSectionEditorChat({
      key: draftKey,
      userTurn: { role: 'user', content: prompt, llmContent: prompt },
      contextBundle: bundle,
      questions: sectionQuestions as FormTreeQuestion[],
      requireFullLayout: true,
      toast,
    });
  }, [
    chatSending,
    hasCoverageGapsForAssistant,
    fieldCoverage,
    parsedLayout.ok,
    strayLayoutQids,
    radioOptionCoverageIssues,
    cssHiddenCoverageIssues,
    schemaGateCoverageIssues,
    missingCmsVisibleLabelIssues,
    buildContextBundleOrNull,
    sendSectionEditorChat,
    draftKey,
    sectionQuestions,
    toast,
  ]);

  const selectedPick =
    schemaPickId.trim() !== ''
      ? studioSchemaPicks.find(p => p.id === schemaPickId)
      : undefined;

  const handleSubmit = async () => {
    if (layoutParsePending) return;
    if (!parsedLayout.ok) return;
    const uncoveredCount = fieldCoverage?.uncovered.length ?? 0;
    const radioMismatchCount = radioOptionCoverageIssues.length;
    const cssHiddenCount = cssHiddenCoverageIssues.length;
    const cmsVisibleLabelCount = missingCmsVisibleLabelIssues.length;
    if (
      uncoveredCount > 0 ||
      radioMismatchCount > 0 ||
      cssHiddenCount > 0 ||
      cmsVisibleLabelCount > 0
    ) {
      const lines: string[] = [];
      if (uncoveredCount > 0) {
        lines.push(
          `${uncoveredCount} schema question(s) have no matching answer control in this layout (static field coverage).`
        );
      }
      if (cssHiddenCount > 0) {
        lines.push(
          `${cssHiddenCount} required question(s) appear hidden with CSS (display:none, hidden, invisible, opacity:0, etc.).`
        );
      }
      if (radioMismatchCount > 0) {
        lines.push(
          `${radioMismatchCount} radio question(s) have table radio-option codes that do not match schema values[] (missing or extra).`
        );
      }
      if (cmsVisibleLabelCount > 0) {
        lines.push(
          `${cmsVisibleLabelCount} lettered CMS item id(s) have layout controls but no visible label text in the layout (e.g. add "(GG0100)" once in a static or section-label—not GG0100A/B on each answer): ${missingCmsVisibleLabelIssues.map(id => `(${id})`).join(', ')}.`
        );
      }
      lines.push('Save anyway?');
      if (!window.confirm(lines.join('\n\n'))) {
        return;
      }
    }

    const layout = parsedLayout.layout;

    if (isEditingPublishedJson) {
      try {
        await onSaveJson({
          layout_json: layout,
          name: name || undefined,
          description: description || undefined,
          status: layoutStatus,
        } satisfies BespokeTemplateUpdate);
      } catch {
        // Parent save handler owns the error toast.
      }
      return;
    }

    const pickSave =
      schemaPickId && studioSchemaPicks.find(p => p.id === schemaPickId);

    if (!pickSave || !sectionName.trim() || !name.trim()) return;
    if (!pickSave.schemaVersion.version) {
      toast.error(
        'Missing schema version',
        'The selected schema is missing a version identifier and cannot be saved.'
      );
      return;
    }

    try {
      await onSaveJson({
        form_type: pickSave.rogerDocFormType,
        schema_version_name: pickSave.schemaVersion.name,
        form_schema_version: pickSave.schemaVersion.version,
        section_name: sectionName.trim(),
        layout_json: layout,
        name,
        description: description || undefined,
        status: layoutStatus,
      } satisfies BespokeTemplateCreate);
    } catch {
      // Parent save handler owns the error toast.
    }
  };

  const canSubmitCreate =
    !!selectedPick &&
    !!sectionName.trim() &&
    !!name.trim() &&
    layoutJsonText.trim().length > 0 &&
    !layoutParsePending &&
    parsedLayout.ok;

  const canSubmitEdit =
    isEditingPublishedJson &&
    !!name.trim() &&
    layoutJsonText.trim().length > 0 &&
    !layoutParsePending &&
    parsedLayout.ok;

  const saveDisabled =
    saving ||
    !isDirty ||
    (!isEditingPublishedJson && !canSubmitCreate) ||
    (isEditingPublishedJson && !canSubmitEdit);

  /** Restore all editable fields to the last loaded/saved snapshot. */
  const handleRevert = () => {
    revertSectionEditorDraft(draftKey);
  };

  /** True when the loaded section layout has no coverage/validation gaps. */
  const validationPassing =
    parsedLayout.ok &&
    !layoutParsePending &&
    !questionsLoading &&
    sectionQuestions.length > 0 &&
    !hasCoverageGapsForAssistant;

  const previewSectionName =
    sectionName.trim() || initial?.section_name?.trim() || 'preview';
  const previewSchemaReady =
    questionsResolved && !questionsLoading && sectionQuestions.length > 0;
  const previewSchemaLoading = questionsLoading || !questionsResolved;
  const previewLayoutEmpty =
    layoutJsonText.trim() === '' ||
    (parsedLayout.ok && parsedLayout.layout.length === 0);

  const chatDraftHasContent =
    chatInput.trim().length > 0 ||
    chatImages.length > 0 ||
    pickedPreviewElement != null;

  return (
    <>
      <div className='flex min-h-0 flex-1 flex-col'>
        {/* Header — section label + Save (visit-page top-right position). */}
        <div className='flex flex-wrap items-center justify-between gap-2 pb-3'>
          <div className='min-w-0'>
            {targetSectionName ? (
              <>
                <h2
                  className='truncate text-lg font-semibold'
                  title={targetSectionName}
                >
                  {formatSectionDropdownLabel(targetSectionName)}
                </h2>
                {targetSchemaVersionName && (
                  <p className='text-xs text-muted-foreground'>
                    {targetSchemaVersionName}
                  </p>
                )}
              </>
            ) : (
              <h2 className='text-lg font-semibold'>Layout editor</h2>
            )}
          </div>
          <div className='flex items-center gap-2'>
            {remotePush ? (
              <>
                <div
                  className='flex items-center gap-1.5 rounded-md border border-teal-200 bg-teal-50 px-2.5 py-1.5 text-xs text-teal-800 shadow-sm'
                  title='Pushed from Forms Manager MCP since this layout was last loaded here.'
                >
                  <RefreshCw
                    className='h-3.5 w-3.5 shrink-0 text-teal-600'
                    aria-hidden
                  />
                  <span className='font-semibold'>
                    Updated remotely{' '}
                    {formatRemotePushTimeLabel(remotePush.updatedAt)}
                  </span>
                </div>
                <div
                  className='hidden h-5 w-px shrink-0 bg-border sm:block'
                  aria-hidden
                />
              </>
            ) : null}
            {inactiveLayoutIndicator ? (
              <>
                <div
                  className='flex items-center gap-1.5 rounded-md border border-purple-200 bg-purple-50 px-2.5 py-1.5 text-xs text-purple-800 shadow-sm'
                  title={inactiveLayoutIndicator.detail}
                >
                  <Layers
                    className='h-3.5 w-3.5 shrink-0 text-purple-600'
                    aria-hidden
                  />
                  <span className='font-semibold'>
                    Editing {inactiveLayoutIndicator.short} layout
                  </span>
                </div>
                <div
                  className='hidden h-5 w-px shrink-0 bg-border sm:block'
                  aria-hidden
                />
              </>
            ) : null}
            <Button
              type='button'
              onClick={handleSubmit}
              disabled={saveDisabled}
            >
              {saving && (
                <Loader2 className='h-4 w-4 animate-spin mr-2' aria-hidden />
              )}
              {isEditingPublishedJson ? 'Save changes' : 'Create'}
            </Button>
            <Button
              type='button'
              variant='outline'
              onClick={handleRevert}
              disabled={saving || !isDirty}
            >
              Revert changes
            </Button>
          </div>
        </div>

        {layoutCompileError && (
          <div
            role='alert'
            className='mb-3 flex gap-2 rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2.5'
          >
            <AlertTriangle
              className='mt-0.5 h-4 w-4 shrink-0 text-destructive'
              aria-hidden
            />
            <div className='min-w-0 text-xs'>
              <p className='font-medium text-destructive'>
                Layout does not compile
              </p>
              <p className='mt-1 text-destructive/90'>{layoutCompileError}</p>
            </div>
          </div>
        )}

        {/* Validation — occupies the visit page's Live Scrubber slot. */}
        <div className='mb-3 rounded-md border bg-muted/20'>
          <div className='flex w-full items-center justify-between gap-3 p-3'>
            <button
              type='button'
              onClick={() => setValidationOpen(v => !v)}
              className='flex min-w-0 flex-1 flex-wrap items-center gap-2 text-left text-sm'
            >
              {validationPassing ? (
                <CheckCircle2 className='h-4 w-4 shrink-0 text-green-600' />
              ) : (
                <AlertTriangle className='h-4 w-4 shrink-0 text-amber-600' />
              )}
              <span className='font-medium'>Validation</span>
              {sectionQuestions.length > 0 &&
                parsedLayout.ok &&
                !layoutParsePending && (
                  <>
                    <Badge variant='outline' className='text-xs font-mono'>
                      {!fieldCoverage
                        ? '—'
                        : matchedFieldCount != null
                          ? `Fields ${matchedFieldCount}/${targetN}`
                          : '—'}
                    </Badge>
                    {cssHiddenCoverageIssues.length > 0 ? (
                      <Badge
                        variant='destructive'
                        className='text-xs font-mono'
                      >
                        CSS hidden {cssHiddenCoverageIssues.length}
                      </Badge>
                    ) : null}
                    {schemaGateCoverageIssues.length > 0 ? (
                      <Badge
                        variant='destructive'
                        className='text-xs font-mono'
                      >
                        Schema gating {schemaGateCoverageIssues.length}
                      </Badge>
                    ) : null}
                    {missingCmsVisibleLabelIssues.length > 0 ? (
                      <Badge
                        variant='destructive'
                        className='text-xs font-mono'
                      >
                        CMS labels {missingCmsVisibleLabelIssues.length}
                      </Badge>
                    ) : null}
                  </>
                )}
              {layoutParsePending && (
                <span className='text-xs text-muted-foreground'>Checking…</span>
              )}
              {validationOpen ? (
                <ChevronUp className='h-4 w-4 shrink-0 text-muted-foreground' />
              ) : (
                <ChevronDown className='h-4 w-4 shrink-0 text-muted-foreground' />
              )}
            </button>
            <Button
              type='button'
              variant='secondary'
              size='sm'
              className='h-8 shrink-0 gap-1 text-xs'
              disabled={
                chatSending ||
                !canSendLayoutAssistantMessage ||
                questionsLoading ||
                !hasCoverageGapsForAssistant
              }
              title='Ask the layout assistant to repair missing QIDs, stray QIDs, CSS-hidden QIDs, schema gating issues, and radio-option mismatches.'
              onClick={() => void requestAssistantCoverageFix()}
            >
              {chatSending ? (
                <Loader2 className='h-3.5 w-3.5 animate-spin' aria-hidden />
              ) : (
                <Sparkles className='h-3.5 w-3.5' aria-hidden />
              )}
              Fix coverage
            </Button>
          </div>
          {validationOpen && (
            <div className='max-h-56 space-y-3 overflow-y-auto border-t px-3 py-3'>
              {questionsLoading ? (
                <p className='text-xs text-muted-foreground'>
                  Loading RogerDocs section schema…
                </p>
              ) : sectionQuestions.length === 0 ? (
                <p className='text-xs text-amber-800 dark:text-amber-400'>
                  Pick schema + section to load RogerDoc slice for preview.
                </p>
              ) : null}
              {sectionQuestions.length > 0 &&
                parsedLayout.ok &&
                !layoutParsePending && (
                  <p className='text-xs text-muted-foreground'>
                    Validation runs automatically on update
                  </p>
                )}
              {cssHiddenCoverageIssues.length > 0 &&
                parsedLayout.ok &&
                !layoutParsePending && (
                  <div className='text-xs'>
                    <p className='font-medium text-destructive'>
                      Required QIDs hidden with CSS (
                      {cssHiddenCoverageIssues.length})
                    </p>
                    <ul className='mt-1 max-h-40 list-inside list-disc space-y-1 overflow-auto rounded border bg-background p-2 text-[11px] leading-tight'>
                      {cssHiddenCoverageIssues.map(issue => (
                        <li key={issue.qid}>
                          {formatLayoutPlainCssHiddenSummary(issue)}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              {schemaGateCoverageIssues.length > 0 &&
                parsedLayout.ok &&
                !layoutParsePending && (
                  <div className='text-xs'>
                    <p className='font-medium text-destructive'>
                      Schema parent/child gating (
                      {schemaGateCoverageIssues.length})
                    </p>
                    <ul className='mt-1 max-h-40 list-inside list-disc space-y-1 overflow-auto rounded border bg-background p-2 text-[11px] leading-tight'>
                      {schemaGateCoverageIssues.map(issue => (
                        <li key={`${issue.kind}-${issue.qid}`}>
                          {formatLayoutSchemaGateIssueSummary(issue)}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              {missingCmsVisibleLabelIssues.length > 0 &&
                parsedLayout.ok &&
                !layoutParsePending && (
                  <div className='text-xs'>
                    <p className='font-medium text-destructive'>
                      Lettered CMS item ids missing visible labels (
                      {missingCmsVisibleLabelIssues.length})
                    </p>
                    <pre className='mt-1 max-h-32 overflow-auto rounded border bg-background p-2 text-[11px] leading-tight whitespace-pre-wrap'>
                      {missingCmsVisibleLabelIssues.join(', ')}
                    </pre>
                  </div>
                )}
              {fieldCoverage &&
                fieldCoverage.uncovered.length > 0 &&
                parsedLayout.ok &&
                !layoutParsePending && (
                  <div className='text-xs'>
                    <p className='font-medium text-amber-800 dark:text-amber-400'>
                      Required QIDs missing from Roger Layout Schema (
                      {fieldCoverage.uncovered.length})
                    </p>
                    <pre className='mt-1 max-h-32 overflow-auto rounded border bg-background p-2 text-[11px] leading-tight whitespace-pre-wrap'>
                      {fieldCoverage.uncovered.join(', ')}
                    </pre>
                  </div>
                )}
              {radioOptionCoverageIssues.length > 0 &&
                parsedLayout.ok &&
                !layoutParsePending && (
                  <div className='text-xs'>
                    <p className='font-medium text-amber-800 dark:text-amber-400'>
                      Radio table codes vs schema values[] (
                      {radioOptionCoverageIssues.length})
                    </p>
                    <ul className='mt-1 max-h-40 list-inside list-disc space-y-1 overflow-auto rounded border bg-background p-2 text-[11px] leading-tight'>
                      {radioOptionCoverageIssues.map(issue => (
                        <li key={issue.qid}>
                          <span className='font-mono'>{issue.qid}</span>
                          {issue.missingValues.length > 0 ? (
                            <span className='text-muted-foreground'>
                              {' '}
                              missing: {issue.missingValues.join(', ')}
                            </span>
                          ) : null}
                          {issue.extraValues.length > 0 ? (
                            <span className='text-muted-foreground'>
                              {' '}
                              extra: {issue.extraValues.join(', ')}
                            </span>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              {strayLayoutQids.length > 0 && (
                <div className='text-xs'>
                  <p className='font-medium text-destructive'>
                    QIDs present in Roger Layout Schema that do not belong (
                    {strayLayoutQids.length})
                  </p>
                  <pre className='mt-1 max-h-32 overflow-auto rounded border bg-background p-2 text-[11px] leading-tight whitespace-pre-wrap'>
                    {strayLayoutQids.join(', ')}
                  </pre>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Form display — interactive preview is the main body. No border so it blends into the page. */}
        <div
          ref={previewColumnRef}
          className='group/preview relative flex min-h-[28rem] flex-1 flex-col'
        >
          <div
            ref={previewPaneRef}
            className={[
              'relative flex-1 overflow-auto py-3',
              previewPickerActive ? 'cursor-crosshair select-none' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            onMouseMoveCapture={handlePreviewPickerMove}
            onMouseLeave={() => setPreviewHoverBox(null)}
            onClickCapture={handlePreviewPickerClick}
          >
            {previewPickerActive && previewHoverBox && (
              <div
                className='pointer-events-none absolute z-20 rounded border-2 border-purple-500 bg-purple-500/10'
                style={{
                  left: previewHoverBox.left,
                  top: previewHoverBox.top,
                  width: previewHoverBox.width,
                  height: previewHoverBox.height,
                }}
              />
            )}
            {layoutParsePending ? (
              <div className='flex min-h-[24rem] items-center justify-center'>
                <div className='flex flex-col items-center gap-2 text-sm text-muted-foreground'>
                  <Loader2 className='h-6 w-6 animate-spin' />
                  Updating preview...
                </div>
              </div>
            ) : previewSchemaLoading ? (
              <div className='flex min-h-[24rem] items-center justify-center'>
                <div className='flex flex-col items-center gap-2 text-sm text-muted-foreground'>
                  <Loader2 className='h-6 w-6 animate-spin' />
                  Loading preview schema...
                </div>
              </div>
            ) : !previewSchemaReady ? (
              <p className='text-sm text-muted-foreground'>
                Could not load the section schema for this preview.
              </p>
            ) : previewLayoutEmpty ? (
              <div className='flex min-h-[24rem] items-center justify-center'>
                <p className='text-sm text-muted-foreground/70'>
                  Empty Roger Layout Schema
                </p>
              </div>
            ) : layoutCompileError ? (
              <div className='flex min-h-[24rem] flex-col items-center justify-center gap-2 px-6 text-center'>
                <p className='text-sm font-medium text-destructive'>
                  Layout does not compile
                </p>
                <p className='max-w-md text-sm text-muted-foreground'>
                  {layoutCompileError}
                </p>
              </div>
            ) : parsedLayout.ok ? (
              <BespokeLayoutRenderer
                layout={parsedLayout.layout}
                answers={studioAnswers}
                onAnswerChange={(qid, value) =>
                  setStudioAnswers(prev => ({ ...prev, [qid]: value }))
                }
                onFormDataChange={() => {}}
                schema={sectionQuestions}
                schemaQuestionOptions={[]}
                sectionName={previewSectionName}
              />
            ) : (
              <p className='text-sm text-muted-foreground'>
                Fix Roger Layout Schema to render an interactive preview.
              </p>
            )}
          </div>

          {/* AI chat — fixed just above the chat button (sticks with scroll). */}
          {chatOpen && (
            <div className='fixed bottom-24 right-6 z-50 flex h-[28rem] max-h-[calc(100vh-8rem)] w-[24rem] max-w-[calc(100vw-3rem)] flex-col overflow-hidden rounded-lg border bg-background shadow-xl'>
              <div className='flex shrink-0 items-center justify-between gap-2 border-b px-3 py-2'>
                <div className='min-w-0'>
                  <span className='text-sm font-semibold tracking-tight text-foreground'>
                    Layout assistant
                  </span>
                </div>
                <div className='flex items-center gap-1.5'>
                  <Popover
                    open={settingsPopoverOpen}
                    onOpenChange={setSettingsPopoverOpen}
                  >
                    <PopoverTrigger asChild>
                      <Button
                        type='button'
                        variant='outline'
                        size='sm'
                        className='shrink-0'
                        aria-label='Layout assistant settings'
                      >
                        <Settings className='h-3.5 w-3.5' />
                        <span className='ml-1 hidden sm:inline'>
                          {selectedModel?.label ?? 'Model'}
                        </span>
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent
                      align='end'
                      sideOffset={6}
                      className='w-96 space-y-4'
                    >
                      <div className='space-y-1.5'>
                        <label className='text-xs font-medium text-foreground'>
                          Model
                        </label>
                        <Select
                          value={selectedModel?.id ?? ''}
                          onValueChange={value => setVibecoderModelId(value)}
                          disabled={availableModels.length === 0}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder='Loading models...' />
                          </SelectTrigger>
                          <SelectContent>
                            {availableModels.map(m => (
                              <SelectItem key={m.id} value={m.id}>
                                <span className='inline-flex items-baseline gap-1.5'>
                                  <span className='text-sm font-medium'>
                                    {m.label}
                                  </span>
                                  <span className='text-[11px] uppercase tracking-wide text-muted-foreground'>
                                    {m.provider === 'anthropic'
                                      ? 'Anthropic'
                                      : 'OpenAI'}
                                  </span>
                                </span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {selectedModel?.description && (
                          <p className='text-[11px] text-muted-foreground'>
                            {selectedModel.description}
                          </p>
                        )}
                      </div>

                      {selectedModel?.supports_temperature ? (
                        <div className='space-y-1.5'>
                          <div className='flex items-baseline justify-between gap-2'>
                            <label className='text-xs font-medium text-foreground'>
                              Temperature
                            </label>
                            <span className='font-mono text-[11px] text-muted-foreground'>
                              {effectiveTemperature.toFixed(2)}
                            </span>
                          </div>
                          <input
                            type='range'
                            min={TEMPERATURE_MIN}
                            max={TEMPERATURE_MAX}
                            step={TEMPERATURE_STEP}
                            value={effectiveTemperature}
                            onChange={e =>
                              setVibecoderTemperature(Number(e.target.value))
                            }
                            className='w-full accent-primary'
                            aria-label='Temperature'
                          />
                          <p className='text-[11px] text-muted-foreground'>
                            Lower = more deterministic. Higher = more creative.
                          </p>
                          <Button
                            type='button'
                            variant='ghost'
                            size='sm'
                            onClick={() => setVibecoderTemperature(null)}
                          >
                            Reset to default (
                            {selectedModel.default_temperature.toFixed(2)})
                          </Button>
                        </div>
                      ) : null}
                    </PopoverContent>
                  </Popover>
                  <Button
                    type='button'
                    variant='ghost'
                    size='sm'
                    className='h-8 w-8 shrink-0 p-0'
                    onClick={() => setChatOpen(false)}
                    aria-label='Close layout assistant chat'
                  >
                    <X className='h-4 w-4' />
                  </Button>
                </div>
              </div>
              <div className='min-h-0 flex-1 overflow-y-auto p-2 space-y-2'>
                {chatMessages.length > 0 &&
                  chatMessages.map((m, i) => (
                    <div
                      key={`${m.role}-${i}`}
                      className={`rounded-md px-2 py-1.5 text-[11px] leading-snug whitespace-pre-wrap ${
                        m.role === 'user'
                          ? 'bg-primary/10 ml-4'
                          : 'bg-background mr-4 border'
                      }`}
                    >
                      <span className='font-medium text-[10px] text-muted-foreground block mb-0.5'>
                        {m.role === 'user' ? 'You' : 'Assistant'}
                      </span>
                      {m.content}
                      {m.images && m.images.length > 0 && (
                        <div className='mt-1 flex flex-wrap gap-1'>
                          {m.images.map((img, imgIndex) => (
                            <span
                              key={`${img.filename ?? img.media_type}-${imgIndex}`}
                              className='inline-flex items-center gap-1 rounded border bg-background/70 px-1.5 py-0.5 text-[10px] text-muted-foreground'
                            >
                              <ImageIcon className='h-3 w-3' />
                              {img.filename ?? 'Attached image'}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                {chatSending && chatMessages.length > 0 && (
                  <div className='rounded-md px-2 py-1.5 text-[11px] leading-snug whitespace-pre-wrap bg-background mr-4 border'>
                    <span className='font-medium text-[10px] text-muted-foreground block mb-0.5'>
                      Assistant
                    </span>
                    <span className='inline-flex items-center gap-0.5 text-muted-foreground'>
                      <span className='animate-bounce [animation-delay:-0.2s]'>
                        .
                      </span>
                      <span className='animate-bounce [animation-delay:-0.1s]'>
                        .
                      </span>
                      <span className='animate-bounce'>.</span>
                    </span>
                  </div>
                )}
              </div>
              <div className='shrink-0 border-t p-2'>
                <div className='mb-2 flex items-center gap-2'>
                  <Button
                    type='button'
                    variant={previewPickerActive ? 'default' : 'outline'}
                    size='sm'
                    className='h-7 gap-1 text-[11px]'
                    disabled={!parsedLayout.ok || !previewSchemaReady}
                    onClick={togglePreviewPicker}
                    title='Click an element in the preview to reference it in your message'
                  >
                    <MousePointer2 className='h-3.5 w-3.5' />
                    {previewPickerActive ? 'Click preview…' : 'Pick element'}
                  </Button>
                </div>
                {pickedPreviewElement && (
                  <div className='mb-2 flex flex-wrap gap-1'>
                    <span className='inline-flex max-w-full items-center gap-1 rounded border border-purple-200 bg-purple-50 px-2 py-1 text-[11px] text-purple-800'>
                      <MousePointer2 className='h-3 w-3 shrink-0' />
                      <span className='truncate'>
                        Selected:{' '}
                        {pickedPreviewElement.nearestDataQid ??
                          pickedPreviewElement.tagName}
                      </span>
                      <button
                        type='button'
                        className='rounded p-0.5 hover:bg-purple-100'
                        onClick={() => setPickedPreviewElement(null)}
                        aria-label='Remove selected preview element'
                      >
                        <X className='h-3 w-3' />
                      </button>
                    </span>
                  </div>
                )}
                {chatImages.length > 0 && (
                  <div className='mb-2 flex flex-wrap gap-1'>
                    {chatImages.map((img, imgIndex) => (
                      <span
                        key={`${img.filename ?? img.media_type}-${imgIndex}`}
                        className='inline-flex items-center gap-1 rounded border bg-background px-2 py-1 text-[11px] text-muted-foreground'
                      >
                        <ImageIcon className='h-3 w-3' />
                        {img.filename ?? 'Attached image'}
                        <button
                          type='button'
                          className='rounded p-0.5 hover:bg-muted'
                          onClick={() =>
                            setChatImages(prev =>
                              prev.filter((_, idx) => idx !== imgIndex)
                            )
                          }
                          aria-label='Remove attached image'
                        >
                          <X className='h-3 w-3' />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <div className='flex items-start gap-2'>
                  <Textarea
                    ref={chatTextareaRef}
                    value={chatInput}
                    onChange={e => setChatInput(e.target.value)}
                    placeholder='Describe changes…'
                    rows={1}
                    className='min-h-20 flex-1 resize-none py-2 text-[11px] leading-snug'
                    disabled={chatSending}
                    onKeyDown={e => {
                      const bundleReady = buildContextBundleOrNull() != null;
                      if (
                        e.key === 'Enter' &&
                        !e.shiftKey &&
                        !chatSending &&
                        canSendLayoutAssistantMessage &&
                        chatDraftHasContent &&
                        !questionsLoading &&
                        bundleReady
                      ) {
                        e.preventDefault();
                        void sendVibecoderChat();
                      }
                    }}
                  />
                  <input
                    ref={chatImageInputRef}
                    type='file'
                    accept='image/png,image/jpeg,image/gif,image/webp'
                    className='hidden'
                    onChange={e =>
                      void handleAttachChatImage(e.target.files?.[0])
                    }
                  />
                  <div className='flex shrink-0 flex-col gap-1'>
                    <Button
                      type='button'
                      variant='outline'
                      size='sm'
                      className='h-[38px] w-[38px] p-0'
                      disabled={chatSending}
                      onClick={() => chatImageInputRef.current?.click()}
                      aria-label='Attach image'
                    >
                      <ImageIcon className='h-5 w-5' />
                    </Button>
                    <Button
                      type='button'
                      size='sm'
                      className='h-[38px] w-[38px] p-0'
                      disabled={
                        chatSending ||
                        !chatDraftHasContent ||
                        !canSendLayoutAssistantMessage ||
                        questionsLoading ||
                        buildContextBundleOrNull() == null
                      }
                      onClick={() => void sendVibecoderChat()}
                    >
                      {chatSending ? (
                        <Loader2 className='h-5 w-5 animate-spin' />
                      ) : (
                        <SendHorizonal className='h-5 w-5' />
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <SectionLayoutJsonDialog
        open={layoutJsonOpen}
        onOpenChange={onLayoutJsonOpenChange}
        sectionDisplayName={previewSectionName}
        value={layoutJsonText}
        onApply={next =>
          setSectionEditorField(draftKey, { layoutJsonText: next })
        }
        questions={sectionQuestions}
        onCopyLayoutJson={text => void copyLayoutJsonText(text)}
        onCopyLlmBundle={() => void copyLlmBundle()}
      />

      {/* Chat toggle — fixed bottom-right, always visible so it toggles the window. */}
      <Button
        type='button'
        onClick={() => setChatOpen(v => !v)}
        className='fixed bottom-6 right-6 z-50 h-12 w-12 rounded-full p-0 shadow-lg'
        aria-label={
          chatOpen
            ? 'Close layout assistant chat'
            : 'Open layout assistant chat'
        }
      >
        {chatSending ? (
          <Loader2 className='h-5 w-5 animate-spin' />
        ) : (
          <MessageSquare className='h-5 w-5' />
        )}
      </Button>
    </>
  );
}
